//* Import react and other packages
import { useEffect, useState } from "react";

//* Import components from react-bootstrap
import { Button, Form, Spinner, Alert } from "react-bootstrap";

//* Import components
import { Attention } from "../../../component/base/Attention";

//* Import hooks from react-router-dom
import { useNavigate, useParams } from "react-router-dom";

//* Import icons from material-ui
import { ArrowBack, SaveRounded } from "@material-ui/icons";

//* Import custom hooks
import { useUnits } from "../../../hooks/units/useUnits";

export function UnitForm({ editMode }: { editMode?: boolean }) {
  const navigate = useNavigate();
  const params = useParams();
  const token = localStorage.getItem("token") || "";

  const [formData, setFormData] = useState({
    name: "",
    description: "",
  });
  const [setOriginalData] = useState<any>(null);
  const [isLoadingData, setIsLoadingData] = useState(false);

  // Estado para gerenciar erros de validação
  const [nameError, setNameError] = useState<string>("");

  const { getUnitById, createUnit, updateUnit, isCreating, isUpdating, error } =
    useUnits(token);

  // Function to validate the name field
  const validateName = (name: string): boolean => {
    if (name.trim().length < 3) {
      setNameError("O nome deve ter pelo menos 3 caracteres.");
      return false;
    } else {
      setNameError("");
      return true;
    }
  };

  // Fetch unit data in edit mode
  useEffect(() => {
    const fetchUnitData = async () => {
      if (editMode && params.id) {
        setIsLoadingData(true);
        try {
          const unitData = await getUnitById(params.id);
          if (unitData) {
            const newFormData = {
              name: unitData.name || "", 
              description: unitData.description || "",
            };
            setFormData(newFormData);
          }
        } catch (error) {
          console.error("Error fetching admin data:", error);
        } finally {
          setIsLoadingData(false);
        }
      }
    };

    fetchUnitData();
  }, [editMode, params.id, getUnitById, setOriginalData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form data
    const isNameValid = validateName(formData.name);
    if (!isNameValid) {
      return;
    }

    try {
      if (editMode && params.id) {
        await updateUnit({
          id: params.id,
          name: formData.name,
          description: formData.description,
        });
        navigate("/units");
      } else {
        await createUnit({
          name: formData.name,
          description: formData.description,
        });
        navigate("/units");
      }
    } catch (err) {
      console.error("Operation failed:", err);
    }
  };

  const isLoading = isCreating || isUpdating || isLoadingData;

  return (
    <div className="row">
      <div className="col-12">
        <div className="py-5 px-3">
          <div className="card-body" id="card">
            <h4 className="mt-0 header-title">
              {editMode ? "Editar" : "Cadastrar"} Unidade
            </h4>
            <p>
              {editMode
                ? "Edite uma unidade do seu sistema"
                : "Cadastre uma unidade do seu sistema"}
            </p>
            <br />

            {!isLoading && (
              <Form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-lg">
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Nome <Attention />
                      </Form.Label>
                      <Form.Control
                        placeholder="Nome"
                        value={formData.name}
                        onChange={(e) => {
                          setFormData({ ...formData, name: e.target.value });
                          validateName(e.target.value);
                        }}
                        isInvalid={!!nameError}
                        required
                      />
                      <Form.Control.Feedback type="invalid">
                        {nameError}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </div>

                  <div className="col-lg">
                    <Form.Group className="mb-3">
                      <Form.Label>Descrição</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="Descrição"
                        value={formData.description}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            description: e.target.value,
                          })
                        }
                      />
                    </Form.Group>
                  </div>
                </div>

                <div className="w-100 d-flex justify-content-between mt-3">
                  <Button
                    variant="secondary"
                    className="d-flex align-items-center"
                    onClick={() => navigate("/units")}
                    type="button"
                  >
                    <ArrowBack className="me-2" /> Voltar
                  </Button>
                  <Button
                    variant="success"
                    type="submit"
                    disabled={isLoading}
                    className="d-flex align-items-center"
                  >
                    <SaveRounded className="me-2" /> Salvar
                  </Button>
                </div>
              </Form>
            )}

            {isLoading && (
              <div className="d-flex justify-content-center align-items-center mt-3">
                <Spinner animation="border" variant="primary" role="status" />
              </div>
            )}

            {error && (
              <Alert variant="danger" className="mt-3">
                {error}
              </Alert>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
